import React, { useState, useEffect } from 'react';
import { X, ClipboardList, Pencil, Check } from 'lucide-react';
import {
  ProgramacionService,
  type ProgramacionAsignacion,
} from '../../services/programacion/programacion.service';
import { usePermissions } from '../../hooks/usePermissions';
import { parseFechaLocal } from '../../utils/date.utils';
import {
  EstadoSeguimientoBadge,
  OPCIONES_SEGUIMIENTO,
  normalizarEstado,
  type EstadoSeguimiento,
} from './EstadoSeguimientoBadge';

interface AsignacionDetailsDrawerProps {
  isOpen: boolean;
  asignacion: ProgramacionAsignacion | null;
  onClose: () => void;
  onEdit: () => void;
  /** Se llama tras guardar el seguimiento para que el padre refresque los datos */
  onSeguimientoActualizado?: () => void;
}

const DIAS_SEMANA = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

const formatearFecha = (iso: string): string => {
  const d = parseFechaLocal(iso);
  return `${d.getDate()} de ${MESES[d.getMonth()]} de ${d.getFullYear()}`;
};

const diaDeFecha = (iso: string): string => {
  const d = parseFechaLocal(iso);
  return DIAS_SEMANA[d.getDay()] ?? '';
};

export const AsignacionDetailsDrawer: React.FC<AsignacionDetailsDrawerProps> = ({
  isOpen,
  asignacion,
  onClose,
  onEdit,
  onSeguimientoActualizado,
}) => {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('Programación Semanal', 'editar');

  const [isVisible, setIsVisible] = useState(false);
  const [estadoSel, setEstadoSel] = useState<EstadoSeguimiento>('pendiente');
  const [novedadText, setNovedadText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Sincronizar el editor con la asignación seleccionada
  useEffect(() => {
    if (asignacion) {
      setEstadoSel(normalizarEstado(asignacion.estado));
      setNovedadText(asignacion.novedad ?? '');
      setSaved(false);
      setSaveError(null);
    }
  }, [asignacion]);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(timer);
    }
    setIsVisible(false);
  }, [isOpen]);

  if (!isOpen || !asignacion) return null;

  const handleSaveSeguimiento = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      await ProgramacionService.actualizarSeguimiento(asignacion.id, {
        estado: estadoSel,
        novedad: novedadText.trim() || '',
      });
      setSaved(true);
      onSeguimientoActualizado?.();
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Error al guardar el seguimiento');
    } finally {
      setIsSaving(false);
    }
  };

  const campos = [
    { label: 'Fecha', value: formatearFecha(asignacion.fecha) },
    { label: 'Día', value: diaDeFecha(asignacion.fecha) || null },
    { label: 'Horario', value: `${asignacion.horaInicio} – ${asignacion.horaFin}` },
    { label: 'Participante', value: asignacion.participante?.nombreCompleto ?? null },
    { label: 'Ministerio', value: asignacion.ministerio?.nombre ?? null },
    { label: 'Líder a cargo', value: asignacion.lider?.nombreCompleto ?? null },
    { label: 'Rol', value: asignacion.rol ?? null },
    { label: 'Actividad', value: asignacion.actividad ?? null },
  ];

  return (
    <div className="fixed inset-0 z-[70] overflow-hidden">
      <div
        className={`absolute inset-0 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />

      <section
        className={`absolute right-0 top-0 h-full w-full max-w-2xl bg-white border-l border-gray-200 shadow-2xl transform transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-sm font-medium text-gray-900">Detalle de asignación</h2>
                <p className="text-xs text-gray-500">
                  {diaDeFecha(asignacion.fecha)} {formatearFecha(asignacion.fecha)} · {asignacion.horaInicio} – {asignacion.horaFin}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Contenido */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-5">
              {campos.map((c) => (
                <div key={c.label}>
                  <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">{c.label}</p>
                  <p className="text-xs text-gray-900 mt-1">{c.value ?? '—'}</p>
                </div>
              ))}
            </div>

            <div className="mt-5 pt-5 border-t border-gray-100">
              <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">Observaciones</p>
              <p className="text-xs text-gray-600 mt-1 whitespace-pre-line">
                {asignacion.observaciones ?? '—'}
              </p>
            </div>

            {/* Seguimiento */}
            <div className="mt-5 pt-5 border-t border-gray-100">
              <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-3">
                Seguimiento
              </p>
              {canEdit ? (
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {OPCIONES_SEGUIMIENTO.map((o) => (
                      <button
                        key={o.value}
                        type="button"
                        onClick={() => setEstadoSel(o.value)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                          estadoSel === o.value
                            ? o.value === 'cumplida'
                              ? 'bg-green-600 text-white border-green-600'
                              : o.value === 'no_cumplida'
                                ? 'bg-red-600 text-white border-red-600'
                                : 'bg-purple-600 text-white border-purple-600'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                  <textarea
                    value={novedadText}
                    onChange={(e) => setNovedadText(e.target.value)}
                    rows={2}
                    placeholder="Novedad o incidencia (opcional)…"
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 outline-none bg-white resize-none"
                  />
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={handleSaveSeguimiento}
                      disabled={isSaving}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium bg-purple-600 text-white hover:bg-purple-700 transition-colors disabled:opacity-50"
                    >
                      <Check className="w-3.5 h-3.5" />
                      {isSaving ? 'Guardando…' : 'Guardar seguimiento'}
                    </button>
                    {saved && (
                      <span className="text-[11px] text-green-600 font-medium">
                        Guardado correctamente
                      </span>
                    )}
                    {saveError && <span className="text-[11px] text-red-600">{saveError}</span>}
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <EstadoSeguimientoBadge estado={asignacion.estado} />
                  {asignacion.novedad && (
                    <p className="text-xs text-gray-600 whitespace-pre-line">{asignacion.novedad}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          {canEdit && (
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end shrink-0">
              <button
                onClick={onEdit}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium bg-purple-600 text-white hover:bg-purple-700 transition-colors"
              >
                <Pencil className="w-3.5 h-3.5" />
                Editar programación
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
